import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import HQMark from "@/components/HQMark";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0B0E14] text-white flex" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="login-page">
      <div className="hidden md:flex flex-1 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1638376776402-9a4b75fe21bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRhcmslMjBncmFkaWVudCUyMHRleHR1cmV8ZW58MHx8fHwxNzg1OTI5Mzg5fDA&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14] via-[#0B0E14]/70 to-[#0B0E14]/20" />
        <div className="relative z-10 p-16 flex flex-col justify-between">
          <div className="flex items-center gap-3 text-white">
            <HQMark size={40} />
            <span className="font-display text-lg font-semibold tracking-tight">Alias Distribution HQ</span>
          </div>
          <div className="max-w-lg">
            <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mb-4">
              The executive command center for Alias Distribution.
            </h1>
            <p className="text-white/60 text-base leading-relaxed">
              One private surface for every company, platform, production, and project — with priorities, budgets, and decisions in one place.
            </p>
          </div>
          <div className="text-xs text-white/40 tracking-widest uppercase">hq.aliascompass.com</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-8 py-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-8" data-testid="login-form">
          <div className="md:hidden mb-6 flex items-center gap-3 text-white">
            <HQMark size={40} />
            <span className="font-display text-lg font-semibold tracking-tight">Alias HQ</span>
          </div>
          <div>
            <div className="text-eyebrow mb-3">Executive Access</div>
            <h2 className="font-display text-3xl font-semibold tracking-tight">Sign in to HQ</h2>
            <p className="text-white/50 mt-2 text-sm">Restricted to authorized administrators.</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-white/70 text-xs uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11"
                placeholder="you@aliasdist.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/70 text-xs uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full h-11 rounded-full bg-white text-black hover:bg-white/90 font-medium"
          >
            {loading ? "Signing in…" : "Enter HQ"}
          </Button>
          <div className="text-center">
            <button
              type="button"
              onClick={() => nav("/forgot-password")}
              className="text-xs text-white/50 hover:text-white underline underline-offset-4"
              data-testid="login-forgot-password-link"
            >
              Forgot your password?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
